import { useStatsigClient } from '@statsig/react-bindings'
import {
  BlocksRenderer,
  type BlocksContent,
} from '@strapi/blocks-react-renderer'
import { ReactQuery } from 'cms-api'
import { Formik, Form, Field } from 'formik'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { rem } from 'polished'
import React from 'react'
import { ROUTE, TITLE_OPTIONS } from 'shared-constants'
import styled from 'styled-components'
import * as Yup from 'yup'
import { AttachmentsDropzone } from 'components/AttachmentsDropzone'
import { Button } from 'components/Button'
import { CheckboxGroup } from 'components/Checkbox'
import { AnimatedInputWrapper, Input } from 'components/Input'
import { Box, Flex } from 'components/Layout'
import { LOADER_SIZE } from 'components/Loader'
import { EMPTY_OPTIONS, LocationField } from 'components/LocationField'
import { PhoneInput } from 'components/PhoneInput'
import { Select } from 'components/Select'
import { Toast } from 'components/Toast'
import { Text } from 'components/Typography'
import { WrappedLoader } from 'components/WrappedLoader'
import {
  locationSchema,
  PHONE_REGEX,
  ZIP_CODE_MAX_LENGTH,
} from 'constants/validationSchemas'
import { STATSIG_EVENTS } from 'providers/statsig'
import { COLOR_INTENT, FONT_SIZE, FONT_STACK, FONT_WEIGHT, SPACE } from 'Theme'
import { parseStrapiErrorRequiredFields } from 'utils/parseStrapiErrorFields'

const FORM_ERROR_MESSAGES = {
  uniqueFirmOrExpert: (
    <Text>
      This firm is already active in The Expert Trade Program. Contact your
      principle to be added as a team member. You may email{' '}
      <a href="mailto:trade@theexpert.com">trade@theexpert.com</a> with any
      questions.
    </Text>
  ),
  uniqueEmail: (
    <Text>
      This email is already in use. Please contact{' '}
      <a href="trade@theexpert.com">trade@theexpert.com</a>.
    </Text>
  ),
  alreadyApplied: (
    <Text>
      This email and/or firm has already applied to The Expert Trade Program. To
      inquire about the status of your application, please contact
      <a href="trade@theexpert.com">trade@theexpert.com</a>.
    </Text>
  ),
}

const FILE_TYPE = {
  IMAGE_JPEG: 'image/jpeg',
  IMAGE_PNG: 'image/png',
  PDF: 'application/pdf',
} as const

const ATTACHMENTS_ALLOWED_TYPES = Object.values(FILE_TYPE)

const ListWrapper = styled(Box)`
  ul {
    padding-left: 12px;
  }
  li {
    font-size: ${FONT_SIZE.PX_14};
  }
`

const StyledCheckboxGroup = styled(CheckboxGroup)`
  label {
    width: auto;
  }
`

export const TradeForm: React.FC<{
  description: BlocksContent
  consent: BlocksContent
}> = ({ description, consent }) => {
  const router = useRouter()
  const { client: statsigClient } = useStatsigClient()
  const postTradeApplication = ReactQuery.usePostTradeApplications({
    mutation: {
      onError: (err) => {
        const [error] = parseStrapiErrorRequiredFields(
          err.response?.data?.error
        )
        if (error.name === 'UniqueError') {
          if (error.path === 'businessName') {
            Toast.error(FORM_ERROR_MESSAGES.uniqueFirmOrExpert)
          } else if (error.path === 'expertEmail') {
            Toast.error(FORM_ERROR_MESSAGES.uniqueFirmOrExpert)
          } else if (error.path === 'clientEmail') {
            Toast.error(FORM_ERROR_MESSAGES.uniqueEmail)
          }
        } else if (error.name === 'ValidationError') {
          Toast.error(FORM_ERROR_MESSAGES.alreadyApplied)
        }
      },
    },
  })
  const uploadTradeApplicationAttachments =
    ReactQuery.usePostTradeApplicationUploadDocuments()

  const referralCode = router.query?.code

  const { data: userReferral, isLoading } = ReactQuery.useGetUserReferralsId(
    referralCode as any,
    {
      query: { enabled: !!referralCode },
    }
  )

  const referral = userReferral?.data

  if (isLoading) {
    return <WrappedLoader loaderSize={LOADER_SIZE.XXL} />
  }

  return (
    <Box width="100%" maxWidth={rem(654)}>
      <BlocksRenderer
        content={description}
        blocks={{
          heading: ({ children }) => (
            <Text
              as="h1"
              fontSize={{
                MOBILE: FONT_SIZE.PX_24,
                TABLET: FONT_SIZE.PX_36,
              }}
              color={COLOR_INTENT.GRAY_90}
              fontWeight={FONT_WEIGHT.NORMAL}
              my={SPACE.PX_12}
              textAlign="center"
              fontFamily={FONT_STACK.SERIF}
            >
              {children}
            </Text>
          ),
          list: ({ children }) => (
            <ListWrapper>
              <ul>{children}</ul>
            </ListWrapper>
          ),
          paragraph: ({ children }) => (
            <Text fontSize={FONT_SIZE.PX_14} color={COLOR_INTENT.GRAY_90}>
              {children}
            </Text>
          ),
        }}
      />
      <Formik
        initialValues={{
          businessName: '',
          email: referral?.refereeEmail ?? '',
          firstName: '',
          lastName: '',
          title: '',
          location: {
            country: { isoCode: 'us' },
            state: EMPTY_OPTIONS.STATE_SELECT,
            city: EMPTY_OPTIONS.CITY_SELECT,
          },
          phone: '',
          addressLine1: '',
          addressLine2: null,
          zipCode: '',
          website: '',
          attachments: [],
          areTermsAccepted: false,
        }}
        validationSchema={Yup.object().shape({
          businessName: Yup.string().label('Business name').max(255).required(),
          firstName: Yup.string().label('First name').max(255).required(),
          lastName: Yup.string().label('Last name').max(255).required(),
          email: Yup.string()
            .email('Invalid email')
            .trim()
            .label('Email')
            .required(),
          phone: Yup.string()
            .label('Phone')
            .matches(PHONE_REGEX, 'Phone number is not valid')
            .required(),
          location: Yup.object().shape(locationSchema()),
          title: Yup.string().label('Title').required(),
          addressLine1: Yup.string().trim().label('Address Line 1').required(),
          addressLine2: Yup.string().trim().label('Address Line 2').nullable(),
          zipCode: Yup.string()
            .trim()
            .max(
              ZIP_CODE_MAX_LENGTH,
              `Must be less than ${ZIP_CODE_MAX_LENGTH + 1} characters`
            )
            .label('Zip Code')
            .required(),
          website: Yup.string().required().label('Website'),
          attachments: Yup.array()
            .of(
              Yup.mixed()
                .test(
                  'fileType',
                  'Invalid file format',
                  (value) => value && value instanceof File
                )
                .required('File is required')
            )
            .min(1, 'At least one attachment is required')
            .required('Attachments are required')
            .label('Attachments'),
        })}
        onSubmit={async (values) => {
          if (values && values.areTermsAccepted) {
            // upload attachments
            const attachmentsResponse =
              await uploadTradeApplicationAttachments.mutateAsync({
                data: { files: values.attachments },
              })
            const trimmedEmail = values.email.trim()
            const res = await postTradeApplication.mutateAsync({
              data: {
                data: {
                  attachments:
                    attachmentsResponse.length &&
                    attachmentsResponse.map((attachment) => attachment.id),
                  businessName: values.businessName.trim(),
                  city: values.location.city.value,
                  country: values.location.country.isoCode,
                  state: values.location.state.value,
                  addressLine1: values.addressLine1,
                  addressLine2: values.addressLine2,
                  email: trimmedEmail,
                  firstName: values.firstName,
                  lastName: values.lastName,
                  phoneNumber: values.phone,
                  website: values.website,
                  title: values.title as ReactQuery.TradeApplication['title'],
                  zipCode: values.zipCode,
                  ...(referralCode && {
                    userReferral: referralCode as string,
                  }),
                },
              },
            })
            if (statsigClient) {
              statsigClient.logEvent(
                STATSIG_EVENTS.CREATE_TRADE_APPLICATION,
                trimmedEmail,
                {
                  country: values.location.country.isoCode,
                  firstName: values.firstName,
                  lastName: values.lastName,
                }
              )
            }

            if (res?.data) {
              router.push(
                ROUTE.TRADE_CONFIRMATION({
                  tradeApplicationId: res.data.id,
                })
              )
            }
          }
        }}
      >
        {({ handleSubmit, setFieldValue, values, isSubmitting }) => (
          <Form onSubmit={handleSubmit}>
            <Flex flexDirection="column" mt={SPACE.PX_40}>
              <Field name="email">
                {({ field, meta }) => (
                  <Input
                    label="Email Address for Account Setup*"
                    autocomplete={false}
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched && meta.error ? meta.error : null}
                    hasHint
                    disabled={isLoading || referral}
                    {...field}
                  />
                )}
              </Field>
              <Field name="businessName">
                {({ field, meta }) => (
                  <Input
                    label="Business name*"
                    autocomplete={false}
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched && meta.error ? meta.error : null}
                    hasHint
                    {...field}
                  />
                )}
              </Field>
              <Field name="firstName">
                {({ field, meta }) => (
                  <Input
                    label="First name*"
                    autocomplete={false}
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched && meta.error ? meta.error : null}
                    hasHint
                    {...field}
                  />
                )}
              </Field>
              <Field name="lastName">
                {({ field, meta }) => (
                  <Input
                    label="Last name*"
                    autocomplete={false}
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched && meta.error ? meta.error : null}
                    hasHint
                    {...field}
                  />
                )}
              </Field>
              <Box mb={SPACE.PX_16}>
                <Field name="title">
                  {({ field, meta }) => (
                    <Select
                      name={field.name}
                      label="Title*"
                      options={TITLE_OPTIONS.map((option) => option)}
                      isError={meta.touched && meta.error !== undefined}
                      hint={meta.touched ? meta.error : ''}
                      value={field.value}
                      onChange={({ value }) => {
                        setFieldValue(field.name, value)
                      }}
                    />
                  )}
                </Field>
              </Box>
              <Field name="phone">
                {({ field, meta }) => (
                  <PhoneInput
                    label="Phone Number*"
                    name={field.name}
                    value={field.value}
                    onChange={(value) => {
                      setFieldValue(field.name, value)
                    }}
                    hasHint
                    hint={meta.touched && meta.error ? meta.error : null}
                    isError={meta.touched && meta.error !== undefined}
                    onBlur={field.onBlur}
                  />
                )}
              </Field>
              <Field name="addressLine1">
                {({ field, meta }) => (
                  <Input
                    label="Address Line 1*"
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched && meta.error ? meta.error : null}
                    hasHint={values?.addressLine2 !== null}
                    autoComplete="address-line1"
                    {...field}
                  />
                )}
              </Field>
              <AnimatedInputWrapper
                label="Add address line 2"
                isVisible={values?.addressLine2 !== null}
                setIsVisible={() => {
                  setFieldValue('addressLine2', '')
                }}
              >
                <Field name="addressLine2">
                  {({ field, meta }) => (
                    <Input
                      label="Address Line 2 (optional)"
                      isError={meta.touched && meta.error !== undefined}
                      hint={meta.touched && meta.error ? meta.error : null}
                      hasHint
                      autoComplete="address-line2"
                      {...field}
                    />
                  )}
                </Field>
              </AnimatedInputWrapper>
              <Field name="zipCode">
                {({ field, meta }) => (
                  <Input
                    label="ZIP Code*"
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched && meta.error ? meta.error : null}
                    hasHint
                    autoComplete="postal-code"
                    {...field}
                  />
                )}
              </Field>
              <Field name="location">
                {(props) => (
                  <LocationField {...{ ...props, areRequiredFields: true }} />
                )}
              </Field>
              <Field name="website">
                {({ field, meta }) => (
                  <Input
                    label="Website* "
                    autocomplete={false}
                    isError={meta.touched && meta.error !== undefined}
                    hint={
                      meta.touched && meta.error
                        ? meta.error
                        : "If you don't have a website, leave a link to your social media account."
                    }
                    hasHint
                    {...field}
                  />
                )}
              </Field>
              <Box mt={SPACE.PX_15}>
                <Field name="attachments">
                  {({ field, meta }) => (
                    <AttachmentsDropzone
                      field={field}
                      setFieldValue={setFieldValue}
                      values={values}
                      acceptTypes={ATTACHMENTS_ALLOWED_TYPES}
                      label="Business License, Proof of Registration, or ASID Card*"
                      error={meta.touched && meta.error ? meta.error : null}
                    />
                  )}
                </Field>
              </Box>
              <Field type="checkbox" name="areTermsAccepted">
                {({ field: { value, ...restField }, meta }) => (
                  <StyledCheckboxGroup>
                    <CheckboxGroup.Item
                      isChecked={value}
                      isError={meta.touched && meta.error !== undefined}
                      {...restField}
                    >
                      <BlocksRenderer
                        content={consent}
                        blocks={{
                          paragraph: ({ children }) => (
                            <Text
                              fontSize={FONT_SIZE.PX_14}
                              color={COLOR_INTENT.GRAY_90}
                            >
                              {children}
                            </Text>
                          ),
                          link: ({ url, children }) => (
                            <Link href={url} passHref>
                              {children}
                            </Link>
                          ),
                        }}
                      />
                    </CheckboxGroup.Item>
                  </StyledCheckboxGroup>
                )}
              </Field>
              <Flex mt={SPACE.PX_15} display="flex" justifyContent="flex-start">
                <Button
                  isLoading={isSubmitting}
                  disabled={!values.areTermsAccepted || isSubmitting}
                  type="submit"
                  label="Submit Trade Application"
                  margin={0}
                >
                  Submit
                </Button>
              </Flex>
            </Flex>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
